/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useHistory } from 'react-router-dom';
import { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { Checkbox } from 'antd';
import { capitalize } from 'lodash';
import { useIntl } from 'react-intl';
import Table, { TableProps } from 'antd/lib/table';

// local imports
import { useStyles } from '~src/hooks/useStyles';
import { useProjectId } from '~src/hooks/useProjectId';
import {
  dbTenderShareToTableEntry,
  sortByNumberProp,
  TableEntry,
} from './FileTable.utils';
import IconButton from '~components/_common/IconButton/IconButton';
import { styleFn } from './FileTable.style';
import { FileTableProps } from './FileTable';
import FileVersionsContext from '~src/contexts/FileVersionsContext';
import { formatDate } from '~src/utils/formatDate';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import { useProjectList } from '~src/hooks/api-hooks/useProjectList';
import TenderRegistration from '~src/assets/nav-icons/file-tender-hammer.svg';
import { useShares } from '~src/hooks/api-hooks/useShares';
import { useArchiveSettings } from '~src/hooks/api-hooks/useArchiveSettings';
import { useProject } from '~src/hooks/api-hooks/useProject';
import { DbShareListItem } from '~src/types/db';
import { DB_STATE } from '~src/types/enums';
import { SearchContext } from '~src/contexts/SearchContext';
import { filterByQuery } from '~src/utils/searchFilters';
import {
  getDBStateVisualIntl,
  isShareKeyAccessible,
} from '~src/utils/shareUtils/useShareKeysStatusUtils';

// import { getShareKeyVisual } from '...'; // <- ти сам підставиш імпорт

const sortByUpdatedAt = sortByNumberProp('updatedAt');

const buildShareIdKey = (shareId: any, shareKeyId: any) =>
  `${String(shareId)}-${String(shareKeyId ?? 'nokey')}`;

export const ProjectWebMyTendersTable: React.FC<FileTableProps> = ({
  selectedKeys,
  highlightedRow,
  focusOptions,
  onSelect,
  onSelectAll,
  setSelectedItems,
  handleRow,
  ...tableProps
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();
  const projectId = useProjectId();
  const history = useHistory();
  const { fileVersionsContext } = useContext(FileVersionsContext);

  const [filteredTendersList, setFilteredTendersList] = useState<
    DbShareListItem[]
  >([]);

  const [showRejected, setShowRejected] = useState(false);

  const projectListQuery = useProjectList({
    // onError: () => history.replace(`/projects/${projectId}`),
    refetchOnMount: false,
  });

  const archiveSettings = useArchiveSettings();
  const projectQuery = useProject();
  const isTenderingOn =
    archiveSettings.data?.resArchiveSettings?.is_tendering_on &&
    (projectQuery?.data?.is_tender_manager || projectQuery?.data?.is_superuser);

  const shareTendersQuery = useShares({}, { enabled: isTenderingOn });

  const searchContext = useContext(SearchContext);

  // Convert API shares list into FileTable entries
  // Make key/path unique by using share_id + share_key_id, same as left nav.
  const foldersSource = useMemo(() => {
    return filteredTendersList.map((item) => {
      const shareKeyId = (item as any)?.key?.share_key_id;
      const shareIdKey = buildShareIdKey((item as any)?.share_id, shareKeyId);

      const entry = dbTenderShareToTableEntry(item);

      // Primary-/Secondary Share Keys: block opening a child share whose
      // parent inviter has been rejected/revoked, or whose own key is
      // removed/deleted/expired - mirrors backend's access-check logic.
      const isAccessible = isShareKeyAccessible((item as any)?.key);

      return {
        ...entry,
        key: shareIdKey,
        path: `/my-tenders/${shareIdKey}`,
        disabled: entry.disabled || !isAccessible,

        // dbTenderShareToTableEntry may not keep the original object anywhere,
        // so attach it explicitly for columns renderers.
        // @ts-ignore
        __share: item,
      } as TableEntry;
    });
  }, [filteredTendersList]);

  const renderStateText = (record: TableEntry) => {
    const share = (record as any)?.__share;

    if (!isShareKeyAccessible(share?.key)) {
      return (
        <span style={{ color: 'red', fontWeight: 600 }}>
          {intl.formatMessage({ id: 'tender-share-access-revoked' })}
        </span>
      );
    }

    const raw =
      // flattened in our mapping
      share?.share_key_state ?? share?.key?.share_key_state;

    const stateNum = typeof raw === 'number' ? raw : Number(raw);
    const visual = getDBStateVisualIntl(intl, stateNum);

    return (
      <span style={{ color: visual.color, fontWeight: 600 }}>
        {visual.title}
      </span>
    );
  };

  const columns = useMemo<TableProps<TableEntry>['columns']>(
    () => [
      {
        title: intl.formatMessage({ id: 'type' }),
        dataIndex: 'type',
        width: 50,
        align: 'center',
        render: () => {
          return {
            children: (
              <div style={{ marginLeft: '10px', width: 30, height: 30 }}>
                <NavIcon icon={TenderRegistration} />
              </div>
            ),
          };
        },
      },

      {
        title: intl.formatMessage({ id: 'state' }),
        dataIndex: 'state',
        width: 140,
        ellipsis: true,
        render: (_: any, record) => renderStateText(record),
      },

      {
        title: intl.formatMessage({ id: 'title' }),
        dataIndex: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        width: 280,
        ellipsis: true,
        render: (name, record) => (
          <Fragment>
            {name}
            {record.favourite && (
              <IconButton icon={['fas', 'star']} color="yellow" backgroundOff />
            )}
          </Fragment>
        ),
      },
      {
        title: intl.formatMessage({ id: 'description' }),
        dataIndex: 'description',
        sorter: (a, b) => {
          if (!a.description && !b.description) return 0;
          if (!a.description) return -1;
          if (!b.description) return 1;
          return (a.description || '').localeCompare(b.description || '');
        },
        width: 320,
        ellipsis: true,
      },
      {
        title: intl.formatMessage({ id: 'deadline' }),
        width: 200,
        ellipsis: true,
        sorter: (a, b) => {
          const valA =
            (a as any).__share.registration
              ?.registration_instance_deadline_at || 0;
          const valB =
            (b as any).__share.registration
              ?.registration_instance_deadline_at || 0;

          return valA - valB;
        },
        render: (_, record) => {
          const deadlineAt = (record as any).__share.registration
            ?.registration_instance_deadline_at;

          return deadlineAt ? formatDate(deadlineAt, 'short') : '-';
        },
      },
    ],
    [fileVersionsContext, intl, styles.icons, styles.small]
  );

  useEffect(() => {
    if (!shareTendersQuery.data) {
      setFilteredTendersList([]);
      return;
    }

    let mapped: DbShareListItem[] = shareTendersQuery.data.map((item) => {
      const shareKeyState = item.key?.share_key_state;

      return {
        ...item,
        share_title: item.share_title ?? null,
        registration_title: item.registration?.registration_title ?? null,
        share_description: item.share_description ?? '',
        registration_description:
          item.registration?.registration_description ?? '',
        share_state: item.share_state,

        // IMPORTANT: flatten for easy access in table render
        // @ts-ignore
        share_key_state: shareKeyState,

        project_data: {
          updated_at: item.project?.updated_at ?? undefined,
          start_date: item.project?.start_date ?? undefined,
        },
      };
    });

    // Default: hide rejected based on share_key_state
    if (!showRejected) {
      mapped = mapped.filter(
        // @ts-ignore
        (item) => item.share_key_state !== DB_STATE.REJECTED
      );
    }

    if (searchContext?.query) {
      const q = searchContext.query?.trim().toLowerCase();
      mapped = mapped.filter((item) => {
        const title = (
          item?.registration?.registration_title ||
          item.share_title ||
          ''
        )
          .toString()
          .toLowerCase();

        const description = (
          item?.registration?.registration_description ||
          item.share_description ||
          ''
        )
          .toString()
          .toLowerCase();

        return filterByQuery(title, q) || filterByQuery(description, q);
      });
    }

    setFilteredTendersList(mapped);
  }, [shareTendersQuery.data, showRejected, searchContext?.query]);

  return (
    <div>
      <div css={styles.buttonsContainer}>
        <Checkbox
          checked={showRejected}
          onChange={(e) => setShowRejected(e.target.checked)}>
          {capitalize(intl.formatMessage({ id: 'show-rejected' }))}
        </Checkbox>
      </div>
      <Table<TableEntry>
        showSorterTooltip={false}
        css={[
          styles.table,
          tableProps.scroll?.y && {
            '.ant-table-body': {
              height: tableProps.scroll.y,
            },
          },
        ]}
        className="main-content-window"
        pagination={false}
        bordered
        columns={columns}
        dataSource={foldersSource}
        loading={
          projectListQuery.isLoading ||
          projectListQuery.isIdle ||
          shareTendersQuery.isLoading
        }
        onRow={handleRow}
        rowClassName={(record) => {
          let className = '';
          if (record.key === highlightedRow?.key) {
            className += 'ant-table-row-selected' + ' ';
          }
          if (
            record.key === highlightedRow?.key &&
            record.dbFolder &&
            !record.dbFolder.focus
          ) {
            className += 'ant-table-row-selected' + ' ';
          }
          if (record.disabled) {
            className += 'unfinished' + ' ';
          }

          return className;
        }}
        {...tableProps}
      />
    </div>
  );
};
